import React, { useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const iconMap = {
  InfoIcon: <InfoIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />,
  WarningIcon: <WarningAmberIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />,
  WarningAmberIcon: <WarningAmberIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />,
  HelpIcon: <HelpIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />,
  ErrorIcon: <ErrorOutlineIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />,
  CheckCircleIcon: <CheckCircleOutlineIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />,
  CampaignIcon: <CampaignIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />,
};

const renderDisclaimerIcon = (iconName, variant, headerStyle) => {
  let iconColor = 'inherit';
  if (variant === 'map') {
    iconColor = 'white';
  } else if (variant === 'header' && headerStyle === 'minimal') {
    iconColor = 'rgba(0, 0, 0, 0.7)';
  }
  
  const IconComponent = iconMap[iconName] || iconMap.WarningAmberIcon;
  
  return React.cloneElement(IconComponent, {
    sx: {
      ...IconComponent.props.sx,
      color: iconColor
    }
  });
};

const Disclaimer = ({ 
  selectedView, 
  variant = 'header', // 'header' or 'map'
  headerStyle = 'full', // 'full' or 'minimal'
  organizationBranding = null, // Organization branding settings
  sx = {} 
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // Use organization branding disclaimer if provided, otherwise fall back to view or defaults
  const disclaimerTitle = organizationBranding?.disclaimerText || selectedView?.disclaimer_title || "Test Environment";
  const disclaimerBody = organizationBranding?.disclaimerBody || selectedView?.disclaimer_body || "This application is currently in testing and is for demonstration purposes only.";
  const disclaimerIcon = organizationBranding?.disclaimerIcon || selectedView?.disclaimer_icon || 'WarningAmberIcon';

  const baseStyles = {
    display: 'flex', 
    alignItems: 'center',
    gap: '5px',
    px: '3px',
    py: '2px',
    pr: '9px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
  };

  const isMinimalHeader = headerStyle === 'minimal';
  
  const headerStyles = {
    ...baseStyles,
    height: '100%',
    backgroundColor: isMinimalHeader ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    color: isMinimalHeader ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      backgroundColor: isMinimalHeader ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)',
    }
  };

  const mapStyles = {
    ...baseStyles,
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    '&:hover': {
      backgroundColor: 'var(--color-primary)',
      boxShadow: 'inset 0 0 0 100px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.3)',
      color: 'white',
      border: '1px solid rgba(255, 255, 255, 0.3)',
    }
  };

  // Enhanced styles when tooltip is open
  const getEnhancedStyles = (baseStyles) => {
    if (!isTooltipOpen) return baseStyles;
    
    if (variant === 'header') {
      return {
        ...baseStyles,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      };
    } else {
      return {
        ...baseStyles,
        backgroundColor: 'var(--color-primary)',
        boxShadow: 'inset 0 0 0 100px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.4)',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.4)',
      };
    }
  };

  const containerStyles = variant === 'header' ? headerStyles : mapStyles;
  const finalStyles = getEnhancedStyles(containerStyles);

  return (
    <Tooltip 
      title={
        <span 
          style={{
            // lineHeight: '1',
            padding: '3px',
            display: 'block'
          }}
          dangerouslySetInnerHTML={{
            __html: disclaimerBody
          }}
        />
      }
      onOpen={() => setIsTooltipOpen(true)}
      onClose={() => setIsTooltipOpen(false)}
      disableInteractive={true}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            color: 'rgba(0, 0, 0, 0.87)',
            border: '1px solid rgba(0, 0, 0, 0.04)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(8px)',
            borderRadius: '8px',
            fontSize: 'var(--font-size-sm)',
            maxWidth: '300px',
            padding: '16px 20px',
            pointerEvents: 'none',
            '& span': {
            //   lineHeight: '1.8 !important',
            }
          }
        }
      }}
    > 
      <Box 
        sx={{ 
          ...finalStyles,
          ...sx
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {renderDisclaimerIcon(disclaimerIcon, variant, headerStyle)}
        <Typography 
          variant="body2"
          sx={{
            fontWeight: 400,
            letterSpacing: '0.01em',
            fontSize: 'var(--font-size-sm)'
          }}
          dangerouslySetInnerHTML={{
            __html: disclaimerTitle
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default Disclaimer; 